import React from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { Home, CalendarDays, Calendar, Link2, ClipboardList, NotebookTabs } from 'lucide-react';
import CustomerHome from './pages/CustomerHome';
import CustomerProjectSchedule from './pages/CustomerProjectSchedule';
import CustomerProjectCalendar from './pages/CustomerProjectCalendar';
import CustomerProjectLinks from './pages/CustomerProjectLinks';
import CustomerProjectRegisters from './pages/CustomerProjectRegisters';
import CustomerBpa from './pages/CustomerBpa';
import { initialsOf } from './nameInitials';
import ViewAsMenu from './ViewAsMenu';
import { hasCustomerSession, PORTAL_BASENAME } from './portal/customerSession';
import { signOutCurrentSession } from './signOut';
import { useApp } from './appContext';
import { MaintenanceBanner } from './MaintenanceGate';
import IdleLogout from './IdleLogout';
import PulseMark from './PulseMark';
import { COPYRIGHT_NOTICE } from './legalNotice';
import './App.css';

// Same feature gate as App.js's BPA nav item -- an environment with BPA disabled shouldn't offer
// a customer-facing screen for a module that isn't live internally either.
const BPA_ENABLED = process.env.REACT_APP_BPA_ENABLED === 'true';

// The customer-facing shell (Jeremy, 2026-09-01) -- a separate root component from App.js, not a
// role-filtered NAV_ITEMS branch inside it. A customer contact is a different principal type from
// an internal User (ExternalPrincipal, apps/api/src/auth/auth.service.ts), and App.js's header
// mounts several internal-only widgets unconditionally (NotificationBell, SuggestionBox,
// PeterWidget, HelpDrawer) that all call internal-only endpoints -- teaching each of those to
// recognize "no, not this session" would be more invasive than giving customers their own shell.
// AppShell.js is what picks between the two after /auth/me answers.
//
// Grows the same way App.js's NAV_ITEMS does: one more customer-facing screen is one more entry
// here plus one more <Route>, once a second module has a customer-facing version worth shipping.
const NAV_ITEMS = [
  { path: '/', label: 'Home', icon: Home },
  { path: '/schedule', label: 'Project Schedule', icon: CalendarDays },
  // The same dates as the schedule plus everything else that is scheduled against the project,
  // read as a calendar rather than a Gantt.
  { path: '/calendar', label: 'Project Calendar', icon: Calendar },
  { path: '/links', label: 'Project Links', icon: Link2 },
  // One nav item, four read-only tabbed registers inside it, mirroring the internal shell
  // (Jeremy, 2026-09-03). The standalone "Risk Register" page this replaces is now its third tab.
  { path: '/registers', label: 'Project Registers', icon: NotebookTabs },
  ...(BPA_ENABLED
    ? [
        { type: 'section', label: 'Project Workstreams' },
        { path: '/bpa', label: 'Business Process Analysis', icon: ClipboardList },
      ]
    : []),
];

export default function ExternalApp() {
  const { currentUser } = useApp();
  const displayName = currentUser?.name || 'Signed in';
  // Set by the API only for an internal user viewing as a customer (AuthService
  // .buildCustomerPreview) -- never for a real contact.
  const isPreview = !!currentUser?.isPreview;

  // A real customer contact reaches this shell at /portal/*, so its routes are relative to that;
  // an internal user previewing the portal reaches it at the app root, where they already are.
  // One shell, two mount points -- the alternative was duplicating every route definition.
  const basename = hasCustomerSession() ? PORTAL_BASENAME : '/';

  return (
    <Router basename={basename}>
      <div className="app-shell">
        <IdleLogout />
        <MaintenanceBanner />

        <header className="app-header">
          <div className="header-left">
            <div className="header-brand">
              <div className="header-brand-lockup">
                <PulseMark size={38} trim />
                <span className="header-title">Pulse</span>
              </div>
              <span className="header-sub">Aumentum Technologies · Customer Portal</span>
            </div>
          </div>
          <div className="header-right">
            {/* Only an internal user previewing the portal gets the menu -- a real customer
                contact has nothing to switch to, and must never be shown that they might. */}
            {isPreview ? (
              <>
                <span className="header-preview-pill">Customer view</span>
                <ViewAsMenu displayName={displayName} previewing />
              </>
            ) : (
              <>
                <span className="header-user">{displayName}</span>
                <div className="header-avatar">{initialsOf(displayName)}</div>
              </>
            )}
            <button
              className="btn btn-outline"
              style={{ fontSize: 12, padding: '4px 12px', marginLeft: 12 }}
              onClick={() => { signOutCurrentSession(); }}
            >
              Sign out
            </button>
          </div>
        </header>

        <div className="app-body">
          <nav className="side-nav">
            {NAV_ITEMS.map((item) => {
              // Same `{ type: 'section' }` convention as App.js's NAV_ITEMS -- a non-clickable
              // group label rather than a nested array.
              if (item.type === 'section') {
                return <div key={item.label} className="nav-section-label">{item.label}</div>;
              }
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                >
                  <Icon className="nav-icon" size={18} strokeWidth={1.75} aria-hidden />
                  <span className="nav-label">{item.label}</span>
                </NavLink>
              );
            })}
            <div className="nav-logo">
              <PulseMark size={26} trim />
              <div className="nav-logo-text">
                <span className="nav-logo-title">Pulse</span>
                <span className="nav-logo-sub">BY AUMENTUM</span>
              </div>
            </div>
            <div className="nav-copyright">{COPYRIGHT_NOTICE}</div>
          </nav>

          <main className="main-content">
            <Routes>
              <Route path="/" element={<CustomerHome />} />
              <Route path="/schedule" element={<CustomerProjectSchedule />} />
              <Route path="/calendar" element={<CustomerProjectCalendar />} />
              {BPA_ENABLED && <Route path="/bpa" element={<CustomerBpa />} />}
              <Route path="/links" element={<CustomerProjectLinks />} />
              <Route path="/registers" element={<CustomerProjectRegisters />} />
              {/* The portal's old standalone route, kept as a redirect: it has been live since
                  2026-09-02 and a customer may have it bookmarked or in an email from their PM. */}
              <Route path="/risk-register" element={<Navigate to="/registers?tab=raaidd" replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </Router>
  );
}
